"use server";

import { cookies } from "next/headers";
import { db } from "@/lib/db";
import { revalidatePath } from "next/cache";
import {
    ADMIN_COOKIE,
    verifyAdminPassword,
    verifyAdminSession,
    setAdminSessionCookie,
    setAdminPassword,
    clearAdminPassword,
} from "@/lib/admin";

export async function adminLogin(password: string) {
    if (await verifyAdminPassword(password)) {
        await setAdminSessionCookie();
        return { success: true };
    }
    return { success: false, error: "Invalid admin password" };
}

/**
 * Changes the admin password. The new password is hashed and stored in the
 * database, after which the configured ADMIN_PASSWORD is no longer accepted.
 * All other admin sessions are invalidated; this one is re-issued.
 */
export async function changeAdminPassword(currentPassword: string, newPassword: string) {
    try {
        if (!(await verifyAdminSession())) throw new Error("Not authenticated");
        if (!(await verifyAdminPassword(currentPassword))) throw new Error("Current password is incorrect");
        if (newPassword === currentPassword) throw new Error("New password must differ from the current one");

        await setAdminPassword(newPassword);
        await setAdminSessionCookie();
        revalidatePath("/admin");
        return { success: true as const };
    } catch (e) {
        return { success: false as const, error: e instanceof Error ? e.message : "Failed to update password" };
    }
}

/** Drops the database password so the configured ADMIN_PASSWORD applies again. */
export async function revertAdminPasswordToConfig(currentPassword: string) {
    try {
        if (!(await verifyAdminSession())) throw new Error("Not authenticated");
        if (!(await verifyAdminPassword(currentPassword))) throw new Error("Current password is incorrect");

        await clearAdminPassword();
        await setAdminSessionCookie();
        revalidatePath("/admin");
        return { success: true as const };
    } catch (e) {
        return { success: false as const, error: e instanceof Error ? e.message : "Failed to revert password" };
    }
}

export async function adminLogout() {
    const cookieStore = await cookies();
    cookieStore.delete(ADMIN_COOKIE);
    cookieStore.delete("user_id");
    cookieStore.delete("family_id");
}

export async function toggleUserBlock(userId: string, currentStatus: boolean) {
    await db.user.update({
        where: { id: userId },
        data: { isBlocked: !currentStatus }
    });
    revalidatePath("/admin");
    return { success: true };
}

export async function toggleFamilyBlock(familyId: string, currentStatus: boolean) {
    await db.family.update({
        where: { id: familyId },
        data: { isBlocked: !currentStatus }
    });
    revalidatePath("/admin");
    return { success: true };
}

export async function updateFamilyModules(familyId: string, modules: string[]) {
    await db.family.update({
        where: { id: familyId },
        data: { enabledModules: modules }
    });
    revalidatePath("/admin");
    return { success: true };
}

export async function impersonateFamily(familyId: string) {
    const family = await db.family.findUnique({
        where: { id: familyId },
        include: { members: true }
    });

    if (!family || family.members.length === 0) {
        return { success: false, error: "Family not found or has no members" };
    }

    // Try to find the admin member or fallback to the first member
    const targetMember = family.members.find(m => m.role === "admin") || family.members[0];

    const cookieStore = await cookies();
    cookieStore.set("user_id", targetMember.userId, {
        httpOnly: true,
        secure: process.env.NODE_ENV === "production",
        maxAge: 60 * 60 * 24 * 7,
        path: "/"
    });
    cookieStore.set("family_id", familyId, {
        httpOnly: true,
        secure: process.env.NODE_ENV === "production",
        maxAge: 60 * 60 * 24 * 7,
        path: "/"
    });

    return { success: true };
}

export async function stopImpersonating() {
    const cookieStore = await cookies();
    cookieStore.delete("user_id");
    cookieStore.delete("family_id");
    return { success: true };
}
export async function deleteUser(userId: string) {
    // Delete the user record (cascades to FamilyMember, Permission, PropertyOwnership)
    // Transactions and Categories will have recordedByUserId/createdByUserId set to null
    await db.user.delete({
        where: { id: userId }
    });
    revalidatePath("/admin");
    return { success: true };
}

export async function deleteFamily(familyId: string) {
    await db.$transaction(async (tx) => {
        // Leases restrict deletion of units and tenants, so the cascade from
        // Family -> RentalProperty -> PropertyUnit would stop there. Remove the
        // family's leases first (rent demands cascade from leases).
        await tx.leaseAgreement.deleteMany({
            where: { OR: [{ unit: { property: { familyId } } }, { tenant: { familyId } }] }
        });
        // Delete the family record (cascades to all remaining associated data)
        await tx.family.delete({
            where: { id: familyId }
        });
    }, { timeout: 60_000 });
    revalidatePath("/admin");
    return { success: true };
}
